/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path viewcomponents
 * @description Pure for the generic view components
 * @recursive true
 * @ignore __tests__
 */
import { makeStyles, withStyles } from "@material-ui/core";
import { ButtonClassKey } from "@material-ui/core/Button";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import { TabClassKey } from "@material-ui/core/Tab";
import * as CSS from "csstype";
/**
 *
 * CSS rules definitions
 *
 */
type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";
type BreakPointDef =
  | ["up" | "down" | "only", Breakpoint, CSSProps]
  | ["between", Breakpoint, Breakpoint, CSSProps];
type BreakPointDefs = Array<BreakPointDef>;
export type CSSProps =
  | boolean
  | CSS.Properties
  | Partial<{
      breakpoints: BreakPointDefs;
      flip: boolean;
      "&:hover": CSS.Properties;
      "&:focus": CSS.Properties;
      "&::before": CSS.Properties;
      "&::after": CSS.Properties;
      "& .MuiSelect-select": CSS.Properties;
      "& .MuiSelect-select:focus": CSS.Properties;
      "&:nth-child(1)": CSS.Properties;
      [key: string]: BreakPointDefs | CSSProps;
    }>;
/**
 *
 * MUI Component class CSS override types
 *
 */
type ComponentClassTypes = {
  Button: ButtonClassKey;
  Tab: TabClassKey;
};
type ComponentNames = keyof ComponentClassTypes;
type ComponentClassDeclaration<
  K extends ComponentNames,
  T extends ComponentClassTypes[K]
> = {
  [propKey in T]: CSSProps;
};
type ComponentClassDeclarations = {
  [key in ComponentNames]: Partial<
    ComponentClassDeclaration<key, ComponentClassTypes[key]>
  >;
};
type ComponentDeclarations = {
  [scope: string]: Partial<ComponentClassDeclarations>;
};
// Example:
const comps: ComponentDeclarations = {
  megaMenu: {
    Tab: {
      selected: {
        background: "blue",
      },
    },
  },
};
/**
 *
 * Determines whether a variant is enabled in component props
 *
 */
export const isVariantEnabled = (props: any, variantName: string) => {
  return (
    (`variant-${variantName}` in props &&
      props[`variant-${variantName}`] !== false) ||
    props.variant?.includes(variantName) ||
    false
  );
};
/**
 *
 * This function will convert serialized breakpoints for example
 *
 *  [['down', 'md', { color: 'orange' }]]
 *
 * Into objects with calls to Material UI theme breakpoints
 *
 *  {
 *    [theme.breakpoints.down(md)] : { color: 'orange' }
 *  }
 *
 */
// breakpoints:
export const convertBreakpoints = (
  theme: any,
  list: any,
  transform = (one: any) => one
) => {
  const res: any = {};
  if (list) {
    list.forEach(
      (items: [string, string, string, any] | [string, string, any]) => {
        if (items.length === 4) {
          const [, start, end, value] = items;
          res[theme.breakpoints.between(start, end)] = transform(value);
        } else {
          const [keyword, breakname, value] = items;
          res[theme.breakpoints[keyword](breakname)] = transform(value);
        }
      }
    );
  }
  return res;
};
/**
 * Works like makeStyles but allows style overrides from theme
 *
 */
export const makeThemedStyles = <T extends CSSProps>(
  componentPath: string,
  themeConstructor: (theme: any) => T
): (() => ClassNameMap<Extract<keyof T, string>>) => {
  return makeStyles((theme: any) => {
    const parts = componentPath.split(".");
    const componentName = parts[parts.length - 1];
    const styledObj = theme?.disableLocalOverrides
      ? ({} as any)
      : (themeConstructor(theme) as any);
    const res: any = { [componentName]: {}, ...styledObj };
    let themeObj: any = theme.qpr || {};
    componentPath.split(".").forEach((part) => {
      if (themeObj) {
        themeObj = themeObj[part];
      }
    });
    const transformObject = (original: any) => {
      if (!original) {
        return {};
      }
      const obj = { ...original };
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value && typeof value === "string" && value.indexOf("theme") >= 0) {
          const hasImportant = value.indexOf("!important");
          const append = hasImportant > 0 ? " !important" : "";
          const useValue =
            hasImportant > 0 ? value.slice(0, hasImportant) : value;
          const localParts = useValue.split(".");
          let themeObject: any = theme;
          localParts.forEach((partKey, i) => {
            if (i === 0 || !themeObject) {
              return;
            }
            if (partKey.indexOf("(") >= 0) {
              const [fnName, argsParts] = partKey.split("(");
              const [argStr] = argsParts.split(")");
              if (typeof themeObject[fnName] === "function") {
                obj[key] = themeObject[fnName](JSON.parse(argStr)) + append;
              }
            } else if (
              i === localParts.length - 1 &&
              typeof themeObject[partKey] !== "undefined"
            ) {
              obj[key] = themeObject[partKey] + append;
              return;
            }
            themeObject = themeObject[partKey];
          });
        }
      });
      return obj;
    };
    if (themeObj !== theme && themeObj) {
      // base style CSS and breakpoints
      Object.assign(res[componentName], transformObject(themeObj?.css));
      Object.assign(res[componentName], {
        ...convertBreakpoints(
          theme,
          themeObj.css?.breakpoints as any,
          transformObject
        ),
      });
      Object.keys(themeObj)
        .filter((key) => key !== "css")
        .forEach((variantName) => {
          if (!res[variantName]) res[variantName] = {};
          Object.assign(
            res[variantName],
            transformObject(themeObj[variantName]?.css)
          );
          if (themeObj[variantName]?.css?.breakpoints) {
            Object.assign(res[variantName], {
              ...convertBreakpoints(
                theme,
                themeObj[variantName]?.css?.breakpoints as any,
                transformObject
              ),
            });
          }
        });
    }
    return res;
  }) as any;
};
/*
 * Works like makeStyles but allows style overrides from theme
 *
 */
export const excludeKeys = (
  styles: Record<string, string>,
  exclude: string[]
): Record<string, string> => {
  const enabledClasses: {
    [key: string]: string;
  } = {};
  Object.keys(styles || {})
    .filter((key) => !exclude.includes(key))
    .forEach((key) => {
      if ((styles as any)[key]) {
        enabledClasses[key] = (styles as any)[key];
      }
    });
  return enabledClasses;
};
type WithStylesReturns = ReturnType<typeof withStyles>;
type WithStyleElementArg = Parameters<WithStylesReturns>[0];
type WithStyleElementRes = ReturnType<WithStylesReturns>;
export const themedElement = <T extends WithStyleElementArg>(
  componentPath: string,
  element: T
) => {
  return withStyles((theme: any) => {
    const parts = componentPath.split(".");
    const componentName = parts[parts.length - 1];
    const res: any = {};
    let themeObj: any = theme.qpr || {};
    componentPath.split(".").forEach((part) => {
      if (themeObj) {
        themeObj = themeObj[part];
      }
    });
    const transformObject = (original: any) => {
      if (!original) {
        return {};
      }
      const obj = { ...original };
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value && typeof value === "string" && value.indexOf("theme") >= 0) {
          const hasImportant = value.indexOf("!important");
          const append = hasImportant > 0 ? " !important" : "";
          const useValue =
            hasImportant > 0 ? value.slice(0, hasImportant) : value;
          const localParts = useValue.split(".");
          let themeObject: any = theme;
          localParts.forEach((partKey, i) => {
            if (i === 0 || !themeObject) {
              return;
            }
            if (partKey.indexOf("(") >= 0) {
              const [fnName, argsParts] = partKey.split("(");
              const [argStr] = argsParts.split(")");
              if (typeof themeObject[fnName] === "function") {
                obj[key] = themeObject[fnName](JSON.parse(argStr)) + append;
              }
            } else if (
              i === localParts.length - 1 &&
              typeof themeObject[partKey] !== "undefined"
            ) {
              obj[key] = themeObject[partKey] + append;
              return;
            }
            themeObject = themeObject[partKey];
          });
        }
      });
      return obj;
    };
    if (themeObj !== theme && themeObj) {
      // base style CSS and breakpoints
      Object.assign(res, transformObject(themeObj?.css));
      Object.assign(res, {
        ...convertBreakpoints(
          theme,
          themeObj.css?.breakpoints as any,
          transformObject
        ),
      });
      Object.keys(themeObj)
        .filter((key) => key !== "css")
        .forEach((variantName) => {
          if (!res[variantName]) res[variantName] = {};
          Object.assign(
            res[variantName],
            transformObject(themeObj[variantName]?.css)
          );
          if (themeObj[variantName]?.css?.breakpoints) {
            Object.assign(res[variantName], {
              ...convertBreakpoints(
                theme,
                themeObj[variantName]?.css?.breakpoints as any,
                transformObject
              ),
            });
          }
        });
    }
    return res;
  })(element) as T;
};
export const isValidHex = (hex: string) =>
  /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);
export function hexToRgba(hex: string, alpha?: number) {
  if (isValidHex(hex)) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r || 0}, ${g || 0}, ${b || 0}, ${alpha ?? 1})`;
  }
  return "rgba(0, 0, 0, 1)";
}
export const getBackground = (color: string) => {
  switch (color) {
    case "00ACA0":
      return [
        "linear-gradient(90deg, rgba(0, 169, 163, 0.04) 0%, rgba(0, 169, 163, 0.16) 50%, rgba(0, 169, 163, 0.04) 100%)",
        "linear-gradient(90deg, rgba(0, 169, 163, 0.06) 0%, rgba(0, 169, 163, 0.24) 50%, rgba(0, 169, 163, 0.06) 100%)",
      ];
    case "E3B505":
      return [
        "linear-gradient(90deg, rgba(227, 181, 7, 0.04) 0%, rgba(227, 181, 7, 0.16) 50%, rgba(227, 181, 7, 0.04) 100%)",
        "linear-gradient(90deg, rgba(227, 181, 7, 0.06) 0%, rgba(227, 181, 7, 0.24) 50%, rgba(227, 181, 7, 0.06) 100%)",
      ];
    case "E5554F":
      return [
        "linear-gradient(90deg, rgba(189, 41, 44, 0.04) 0%, rgba(189, 41, 44, 0.16) 50%, rgba(189, 41, 44, 0.04) 100%)",
        "linear-gradient(90deg, rgba(189, 41, 44, 0.06) 0%, rgba(189, 41, 44, 0.24) 47.71%, rgba(189, 41, 44, 0.0624) 100%)",
      ];
    default:
      return [
        `linear-gradient(90deg, ${hexToRgba(color, 0.04)} 0%, ${hexToRgba(
          color,
          0.16
        )} 50%, ${hexToRgba(color, 0.04)} 100%)`,
        `linear-gradient(90deg, ${hexToRgba(color, 0.06)} 0%, ${hexToRgba(
          color,
          0.24
        )} 47.71%, ${hexToRgba(color, 0.04)} 100%)`,
      ];
  }
};
